import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useCallback, useEffect, useState } from "react";
import { Button, Typography, Dialog, DialogHeader, DialogBody, DialogFooter, IconButton } from "@material-tailwind/react";
import { DocumentChartBarIcon, XMarkIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Select from "react-select";
import { Table } from "../../../../../../components/table/Table";
import Loading from "../../../../../../components/loading/Loading";
import EditCreateDrawer from "../../../../../../components/edit-create-drawer/EditCreateDrawer";
import ReportForm from "../../../../../../components/data/report/ReportForm";
import ReportListItem from "../../../../../../components/data/report/ReportListItem";
import ReportTemplateForm from "../../../../../../components/data/report/ReportTemplateForm";
import DeleteReportTemplateModal from "./modals/DeleteReportTemplateModal";
import { api } from "../../../../../../services/api/api";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../../../../../services/api/error";
import { t } from "i18next";

export default function AdminReportsTab() {
  const currentLanguage = localStorage.getItem("i18nextLng") || "en";
  const [templates, setTemplates] = useState<EasyTech.ReportTemplate[] | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [reports, setReports] = useState<EasyTech.ReportItem[] | null>(null);
  const [originalReports, setOriginalReports] = useState<EasyTech.ReportItem[] | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isDeleteTemplateOpen, setIsDeleteTemplateOpen] = useState(false);
  const [labelLookup, setLabelLookup] = useState<Record<string, string>>({});

  const TABLE_HEAD = [
    { label: t("settings:reports:fields:columns:order", "ORDER"), orderKey: "order", width: "80px" },
    { label: t("settings:reports:fields:columns:data", "DATA"), orderKey: "data" },
    { label: t("settings:reports:fields:columns:example", "EXAMPLE"), orderKey: null },
    { label: t("settings:reports:fields:columns:actions", "ACTIONS"), orderKey: null, width: "100px", headClassName: "text-center" }
  ];

  useEffect(() => {
    const getAttributes = api.get("admin/workorders/attributes")
      .then((ret) => {
        const attributes = ret as any[];
        const map: Record<string, string> = {};

        attributes.forEach(attr => {
          if (typeof attr === "string") {
            map[attr] = attr;
          } else if (attr && attr.path) {
            let resolvedLabel = attr.label || attr.path;
            if (attr.translations && Array.isArray(attr.translations)) {
              const translation = attr.translations.find(
                (t: any) => t.language?.isoCode === currentLanguage
              );
              if (translation && translation.label) {
                resolvedLabel = translation.label;
              }
            }
            map[attr.path] = resolvedLabel;
          }
        });
        return map;
      })
      .catch(() => ({}));

    const getCompInfos = api.get("admin/compinfos/inputs")
      .then((ret) => {
        const compInfos = ret as EasyTech.CompInfo[];
        const map: Record<string, string> = {};
        compInfos.forEach(ci => {
          const val = ci.label || ci.id;
          if (val) {
            map[val] = ci.label || val;
          }
        });
        return map;
      })
      .catch(() => ({}));

    Promise.all([getAttributes, getCompInfos]).then(([attrMap, compMap]) => {
      setLabelLookup({
        ...attrMap,
        ...compMap
      });
    });
  }, []);

  const loadTemplates = useCallback(() => {
    api.get("admin/report-templates")
      .then(ret => {
        const tps = ret as EasyTech.ReportTemplate[];
        setTemplates(tps);
        if (tps.length > 0 && !selectedTemplateId) {
          const defaultTp = tps.find(t => t.isDefault) || tps[0];
          setSelectedTemplateId(defaultTp.id as string);
        }
      })
      .catch(err => {
        console.error(err);
        setTemplates([]);
      });
  }, [selectedTemplateId]);

  const loadReports = useCallback(() => {
    if (!selectedTemplateId) {
      setReports([]);
      setOriginalReports([]);
      return;
    }
    
    setReports(null);
    api.get(`admin/reports?templateId=${selectedTemplateId}`)
      .then(ret => {
        const data = ret as EasyTech.ReportItem[];
        setReports(data);
        setOriginalReports(JSON.parse(JSON.stringify(data))); // Deep copy for cancellation
      })
      .catch(err => {
        console.error(err);
        setReports([]);
        setOriginalReports([]);
      });
  }, [selectedTemplateId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const createReportItem = (item: EasyTech.ReportItem): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!selectedTemplateId) return reject();
      item.templateId = selectedTemplateId;

      const areaReports = (reports || []).filter(r => r.area === item.area);
      const maxOrder = areaReports.reduce((max, r) => Math.max(max, r.order ?? 0), 0);
      item.order = maxOrder + 1;

      api.post("admin/reports", item).then((ret) => {
        loadReports();
        toast.success(t("settings:reports:addSuccess", "Item added to reports"));
        return resolve();
      }).catch((error) => {
        console.error(error);
        showApiErrorToast(error, { fallbackMessage: t("settings:reports:errors:addItem") });
        return reject();
      });
    });
  };

  const createTemplate = (template: EasyTech.ReportTemplate): Promise<void> => {
    return new Promise((resolve, reject) => {
      api.post("admin/report-templates", template).then((ret: any) => {
        setSelectedTemplateId(ret.id);
        loadTemplates();
        toast.success(t("settings:reports:templateAddSuccess", "Template created"));
        return resolve();
      }).catch((error) => {
        console.error(error);
        showApiErrorToast(error, { fallbackMessage: t("settings:reports:errors:createTemplate") });
        return reject();
      });
    });
  };

  const updateTemplate = (template: EasyTech.ReportTemplate & { newDefaultTemplateId?: string | null }): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!template.name) {
        toast.error(t("settings:reports:templates:validation:nameRequired", "Template name is required."));
        return reject();
      }

      const initialTemplate = templates?.find(t => t.id === template.id);
      if (initialTemplate?.isDefault && !template.isDefault) {
        if (!template.newDefaultTemplateId) {
          toast.error(t("settings:reports:templates:delete:selectDefaultError", "Please select a replacement default report."));
          return reject();
        }

        const newDefaultTemplate = templates?.find(t => t.id === template.newDefaultTemplateId);
        if (newDefaultTemplate) {
          const updatedNewDefault = { ...newDefaultTemplate, isDefault: true };
          api.put(`admin/report-templates/${template.newDefaultTemplateId}`, updatedNewDefault)
            .then(() => {
              const { newDefaultTemplateId, ...payload } = template;
              api.put(`admin/report-templates/${template.id}`, payload)
                .then(() => {
                  loadTemplates();
                  toast.success(t("settings:reports:templateUpdateSuccess", "Template updated"));
                  return resolve();
                })
                .catch((error) => {
                  console.error(error);
                  showApiErrorToast(error, { fallbackMessage: t("settings:reports:errors:updateTemplate") });
                  return reject();
                });
            })
            .catch((error) => {
              console.error(error);
              showApiErrorToast(error, { fallbackMessage: t("settings:reports:errors:setDefaultTemplate") });
              return reject();
            });
        } else {
          return reject();
        }
      } else {
        const { newDefaultTemplateId, ...payload } = template;
        api.put(`admin/report-templates/${template.id}`, payload)
          .then(() => {
            loadTemplates();
            toast.success(t("settings:reports:templateUpdateSuccess", "Template updated"));
            return resolve();
          })
          .catch((error) => {
            console.error(error);
            showApiErrorToast(error, { fallbackMessage: "Error while updating template" });
            return reject();
          });
      }
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent, areaId: string) => {
    const { active, over } = event;
    if (!over || !reports) return;

    if (active.id !== over.id) {
      const areaReports = reports.filter(r => r.area === areaId).sort((a, b) => a.order - b.order);
      const oldIndex = areaReports.findIndex(item => item.id === active.id);
      const newIndex = areaReports.findIndex(item => item.id === over.id);

      const newAreaReports = arrayMove(areaReports, oldIndex, newIndex);
      
      // Update order internally
      newAreaReports.forEach((item, idx) => {
        item.order = idx + 1;
      });
      
      const newReports = reports.map(r => {
        const found = newAreaReports.find(na => na.id === r.id);
        return found ? found : r;
      });
      setReports(newReports);
    }
  };

  const saveReorder = () => {
    if (!reports) return;
    api.put("admin/reports", reports).then(() => {
      toast.success(t("settings:reports:reorderSuccess", "Order saved!"));
      setIsReordering(false);
      loadReports();
    }).catch(error => {
      console.error(error);
      showApiErrorToast(error, { fallbackMessage: t("settings:reports:errors:saveOrder") });
    });
  };

  const cancelReorder = () => {
    if (originalReports) {
      setReports(JSON.parse(JSON.stringify(originalReports)));
    }
    setIsReordering(false);
  };

  const renderTable = (areaReports: EasyTech.ReportItem[], areaId: string) => (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, areaId)}>
      <SortableContext items={areaReports.map(r => r.id as string)} strategy={verticalListSortingStrategy}>
        <Table tableHead={TABLE_HEAD} emptyTable={areaReports.length === 0}>
          {areaReports.map((report, index) => (
            <ReportListItem key={report.id || index} index={index + 1} report={report} isLast={index === areaReports.length - 1} isReordering={isReordering} onChange={loadReports} labelLookup={labelLookup} />
          ))}
        </Table>
      </SortableContext>
    </DndContext>
  );

  const activeTemplate = templates?.find(t => t.id === selectedTemplateId);

  const selectOptions = templates ? templates.map(tp => ({
    value: tp.id as string,
    label: `${tp.name}${tp.isDefault ? ` (${t("settings:reports:templates:defaultSuffix", "default")})` : ""}`
  })) : [];

  const activeOption = selectOptions.find(opt => opt.value === selectedTemplateId) || null;

  return (
    <div>
      <div className="icon-buttons flex justify-between items-center my-4">
        <Typography variant="h5" className="uppercase font-normal text-gray-700">
          {t("settings:reports:title", "REPORTS")}
        </Typography>
          <div className="flex items-center gap-2">
            {templates && templates.length > 0 && (
              <div className="w-64 text-sm text-gray-800">
                <Select
                  options={selectOptions}
                  value={activeOption}
                  onChange={(opt) => setSelectedTemplateId(opt ? opt.value : null)}
                  isDisabled={isReordering}
                  classNamePrefix="react-select"
                  placeholder={t("settings:reports:templateLabel", "Template")}
                />
              </div>
            )}

            {selectedTemplateId && activeTemplate && (
              <>
                <EditCreateDrawer
                  data={activeTemplate}
                  entityName="report-template"
                  FormComponent={ReportTemplateForm}
                  onSubmit={updateTemplate}
                  isNotCreatable={true}
                  isNotEditable={false}
                  drawerTitle={t("settings:reports:templates:editTitle", "Edit Template")}
                  disabled={isReordering}
                  customEditTrigger={(open) => (
                    <IconButton
                      variant="text"
                      className="h-10 w-10 flex items-center justify-center !bg-[#F7F8F8] hover:!bg-gray-200 transition-colors"
                      onClick={open}
                      disabled={isReordering}
                    >
                      <PencilIcon className="h-5 w-5 !text-[#8E95A2]" />
                    </IconButton>
                  )}
                />
                <IconButton
                  variant="text"
                  className="h-10 w-10 flex items-center justify-center !bg-[#F7F8F8] hover:!bg-gray-200 transition-colors"
                  disabled={isReordering}
                  onClick={() => setIsDeleteTemplateOpen(true)}
                >
                  <TrashIcon className="h-5 w-5 !text-[#8E95A2]" />
                </IconButton>
              </>
            )}

            <EditCreateDrawer entityName="report-template"
                              FormComponent={ReportTemplateForm}
                              isNotEditable={true}
                              onSubmit={createTemplate}
                              drawerTitle={t("common:create", "Create")}
                              createButtonText={t("settings:reports:addTemplate", "Create")}
                              disabled={isReordering} />
          </div>
        </div>

      <hr className="my-4 border-gray-300" />

      {activeTemplate && (
        <>
          <div className="flex justify-between items-center my-6">
            <Typography variant="h5" className="font-normal text-gray-800">
              {activeTemplate.name}
              {activeTemplate.isDefault && (
                <span className="text-gray-500 font-light text-xl ml-1">
                  ({t("settings:reports:templates:defaultSuffix", "default")})
                </span>
              )}
            </Typography>

            <div className="flex gap-2">
              {isReordering ? (
                <>
                  <Button variant="text" color="gray" className="btn-cancel font-normal normal-case h-10 px-4 border" onClick={cancelReorder}>
                    {t("common:cancel")}
                  </Button>
                  <Button variant="filled" className="font-normal normal-case h-10 px-4 bg-[#D9EFFF] text-[#41A1FF] shadow-none hover:shadow-md" onClick={saveReorder}>
                    {t("common:save")}
                  </Button>
                  
                  {/* Action buttons kept visible but disabled during reorder */}
                  <EditCreateDrawer entityName="reports"
                                    FormComponent={ReportForm}
                                    isNotEditable={true}
                                    onSubmit={createReportItem}
                                    drawerTitle={t("settings:reports:addTitle", "Add to report")}
                                    createButtonText={t("settings:reports:add", "Add")}
                                    disabled={true} />
                  <IconButton
                    variant="text"
                    className="h-10 w-10 flex items-center justify-center icon-btn !bg-[#F7F8F8] hover:!bg-gray-200 transition-colors"
                    onClick={() => setIsPreviewOpen(true)}
                    disabled={true}
                  >
                    <DocumentChartBarIcon className={`h-5 w-5 ${isReordering ? "" : "!text-[#8E95A2]"}`} />
                  </IconButton>
                </>
              ) : (
                <>
                  {reports && reports.some(r => reports.filter(item => item.area === r.area).length >= 2) && (
                    <Button variant="filled" className="font-normal normal-case h-10 px-4 bg-[#D9EFFF] text-[#41A1FF] shadow-none hover:shadow-md" onClick={() => setIsReordering(true)}>
                      {t("common:reorder", "Reorder")}
                    </Button>
                  )}
                  <EditCreateDrawer entityName="reports"
                                    FormComponent={ReportForm}
                                    isNotEditable={true}
                                    onSubmit={createReportItem}
                                    drawerTitle={t("settings:reports:addTitle", "Add to report")}
                                    createButtonText={t("settings:reports:add", "Add")} />
                  <IconButton
                    variant="text"
                    className="h-10 w-10 flex items-center justify-center !bg-[#F7F8F8] hover:!bg-gray-200 transition-colors"
                    onClick={() => setIsPreviewOpen(true)}
                  >
                    <DocumentChartBarIcon className="h-5 w-5 !text-[#8E95A2]" />
                  </IconButton>
                </>
              )}
            </div>
          </div>
          {reports && reports.length > 0 && (
            <hr className="my-4 border-gray-300" />
          )}
        </>
      )}

      <div className="my-6">
        {templates === null ? (
          <Loading />
        ) : !selectedTemplateId ? (
          templates.length === 0 ? (
            <Typography className="text-gray-500 italic mt-4">{t("settings:reports:noTemplates", "No templates created yet. Please create one to start.")}</Typography>
          ) : (
            <Typography className="text-gray-500 italic mt-4">{t("settings:reports:selectTemplate", "Please select a template to view or manage items.")}</Typography>
          )
        ) : reports ? (
          <>
            {/* Header Section */}
            {(reports.some(r => r.area === "HEADER_LEFT") || reports.some(r => r.area === "HEADER_RIGHT")) && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Typography variant="h6" className="font-normal text-gray-800">{t("settings:reports:areas:header", "Header")}</Typography>
                  <Typography variant="small" className="text-gray-500">{t("settings:reports:areas:headerDesc", "displayed at the top of the report")}</Typography>
                </div>
                <div className="flex gap-6">
                  <div className="flex-1 w-1/2">
                    {reports.some(r => r.area === "HEADER_LEFT") && (
                      <>
                        <Typography variant="h6" className="font-normal text-gray-800 mb-2">{t("settings:reports:areas:left", "Left")}</Typography>
                        {renderTable(reports.filter(r => r.area === "HEADER_LEFT").sort((a, b) => a.order - b.order), "HEADER_LEFT")}
                      </>
                    )}
                  </div>
                  <div className="flex-1 w-1/2">
                    {reports.some(r => r.area === "HEADER_RIGHT") && (
                      <>
                        <Typography variant="h6" className="font-normal text-gray-800 mb-2">{t("settings:reports:areas:right", "Right")}</Typography>
                        {renderTable(reports.filter(r => r.area === "HEADER_RIGHT").sort((a, b) => a.order - b.order), "HEADER_RIGHT")}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Additional Infos Section */}
            {reports.some(r => r.area === "ADDITIONAL_INFOS") && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Typography variant="h6" className="font-normal text-gray-800">{t("settings:reports:areas:additional", "Additional")}</Typography>
                  <Typography variant="small" className="text-gray-500">{t("settings:reports:areas:additionalDesc", "informations displayed after parts list")}</Typography>
                </div>
                {renderTable(reports.filter(r => r.area === "ADDITIONAL_INFOS").sort((a, b) => a.order - b.order), "ADDITIONAL_INFOS")}
              </div>
            )}

            {/* Footer Section */}
            {reports.some(r => r.area === "FOOTER") && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Typography variant="h6" className="font-normal text-gray-800">{t("settings:reports:areas:footer", "Footer")}</Typography>
                  <Typography variant="small" className="text-gray-500">{t("settings:reports:areas:footerDesc", "displayed at the end of report")}</Typography>
                </div>
                {renderTable(reports.filter(r => r.area === "FOOTER").sort((a, b) => a.order - b.order), "FOOTER")}
              </div>
            )}
          </>
        ) : (
          <Loading />
        )}
      </div>

      <Dialog open={isPreviewOpen} handler={() => setIsPreviewOpen(false)} size="xl" className="max-h-[95vh] flex flex-col">
        <DialogHeader className="flex justify-between items-center border-b border-gray-200 pb-4 shrink-0 px-6 bg-blue-gray-50/50">
          <Typography variant="h5" color="blue-gray" className="font-semibold flex items-center gap-2">
            <DocumentChartBarIcon className="h-6 w-6 text-blue-600" />
            {t("settings:reports:previewTitle", "Preview")} - {activeTemplate?.name}
          </Typography>
          <IconButton variant="text" color="blue-gray" onClick={() => setIsPreviewOpen(false)}>
            <XMarkIcon className="h-5 w-5" />
          </IconButton>
        </DialogHeader>
        
        <DialogBody className="overflow-y-auto p-6 bg-gray-100 flex justify-center grow">
          {/* Simulated A4 sheet using exact Thymeleaf layout and CSS structure */}
          {/* eslint-disable i18next/no-literal-string -- static sample/mock preview data mirroring the server-generated PDF report; not localizable UI copy */}
          <div className="w-full max-w-[800px] bg-white border border-gray-300 shadow-xl p-[10mm] text-gray-800 font-sans" style={{ minHeight: "297mm", fontSize: "14px", lineHeight: "1.4" }}>
            
            {/* Logo container placed exactly like Thymeleaf CSS .logo-container { margin-left: 299px; } */}
            <div style={{ marginLeft: "299px", marginBottom: "20px" }}>
              <img src="/assets/logo.png" alt="Chronoflex Logo" style={{ maxHeight: "55px", display: "block" }} />
            </div>

            {/* .intervention-details-grid table exact clone */}
            <table style={{ width: "100%", borderCollapse: "collapse", margin: "20px 0" }}>
              <tbody>
                <tr>
                  {/* Left Realm Info column */}
                  <td style={{ width: "50%", padding: "0 10px", verticalAlign: "top" }}>
                    <h3 style={{ margin: "0 0 5px 0", fontSize: "15px", fontWeight: "bold", color: "#111827" }}>CHRONO FLEX BELGIUM SA/NV</h3>
                    <p style={{ margin: "5px 0", fontSize: "13px", color: "#374151" }}>BERGENSESTEENWEG 65</p>
                    <p style={{ margin: "5px 0", fontSize: "13px", color: "#374151" }}>1651 LOT</p>
                    <p style={{ margin: "5px 0", fontSize: "13px", color: "#374151" }}>Belgium</p>
                  </td>

                  {/* Right Customer Info column */}
                  <td style={{ width: "50%", padding: "0 10px", verticalAlign: "top" }}>
                    <h3 style={{ margin: "0 0 5px 0", fontSize: "15px", fontWeight: "bold", color: "#111827" }}>Client</h3>
                    <p style={{ margin: "5px 0", fontSize: "13px", fontWeight: "600", color: "#111827" }}>CAPPAL S.A.</p>
                    <p style={{ margin: "5px 0", fontSize: "13px", color: "#374151" }}>
                      <span style={{ fontWeight: "600" }}>Contact :</span> David Kaya
                    </p>
                    <p style={{ margin: "5px 0", fontSize: "13px", color: "#374151" }}>16 Rue J-B Vifquain 7110 La Louvière</p>
                  </td>
                </tr>
                
                {/* Second row for Header Left / Header Right info lists */}
                <tr>
                  <td style={{ width: "50%", padding: "15px 10px 0 10px", verticalAlign: "top" }}>
                    {reports && reports.filter(r => r.area === "HEADER_LEFT").length > 0 ? (
                      reports.filter(r => r.area === "HEADER_LEFT").sort((a, b) => a.order - b.order).map((report, idx) => (
                        <p key={idx} style={{ margin: "5px 0", fontSize: "13px", color: "#374151" }}>
                          <span style={{ fontWeight: "600" }}>{labelLookup[report.data] || report.data}</span>
                          <span>: {getMockValue(report.data, currentLanguage) || report.example || "-"}</span>
                        </p>
                      ))
                    ) : (
                      <div className="text-gray-400 italic py-2 text-center border border-dashed border-gray-200 rounded text-xs">
                        {t("settings:reports:noHeaderLeft", "No HEADER_LEFT fields configured")}
                      </div>
                    )}
                  </td>

                  <td style={{ width: "50%", padding: "15px 10px 0 10px", verticalAlign: "top" }}>
                    {reports && reports.filter(r => r.area === "HEADER_RIGHT").length > 0 ? (
                      reports.filter(r => r.area === "HEADER_RIGHT").sort((a, b) => a.order - b.order).map((report, idx) => (
                        <p key={idx} style={{ margin: "5px 0", fontSize: "13px", color: "#374151" }}>
                          <span style={{ fontWeight: "600" }}>{labelLookup[report.data] || report.data}</span>
                          <span>: {getMockValue(report.data, currentLanguage) || report.example || "-"}</span>
                        </p>
                      ))
                    ) : (
                      <div className="text-gray-400 italic py-2 text-center border border-dashed border-gray-200 rounded text-xs">
                        {t("settings:reports:noHeaderRight", "No HEADER_RIGHT fields configured")}
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* .table for Items list exactly formatted like report_template.html */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "20px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f2f2f2" }}>
                  <th style={{ border: "1px solid #ccc", padding: "5px", textAlign: "left", fontWeight: "bold", fontSize: "13px" }}>Détails</th>
                  <th style={{ border: "1px solid #ccc", padding: "5px", textAlign: "left", fontWeight: "bold", fontSize: "13px", width: "120px" }}>Quantité</th>
                </tr>
              </thead>
              <tbody>
                {/* Row 1: Main d'oeuvre */}
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0", fontWeight: "normal" }}>
                      <span>Main d'œuvre</span>
                    </p>
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px", fontWeight: "bold" }}>
                    00:40
                  </td>
                </tr>

                {/* Row 2: Separator MERCEDES-BENZ CAMION GRUE */}
                <tr style={{ backgroundColor: "#eeeeee" }}>
                  <td colSpan={2} style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0" }}>
                      <strong>MERCEDES-BENZ CAMION GRUE (2-GNX-374)</strong>
                    </p>
                  </td>
                </tr>

                {/* Row 3: Fake hose */}
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0" }}>
                      <span className="text-gray-500">Fake hose fakre qr code</span>
                    </p>
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}></td>
                </tr>

                {/* Row 4: 100R7/FURON3/8JUM */}
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0", paddingLeft: "1.5em" }}>
                      <span>100R7/FURON3/8JUM</span>
                    </p>
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    3.0 Units
                  </td>
                </tr>

                {/* Row 5: PK3/8JUM */}
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0", paddingLeft: "1.5em" }}>
                      <span>PK3/8JUM</span>
                    </p>
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    3.0
                  </td>
                </tr>

                {/* Row 6: E16S */}
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0", paddingLeft: "1.5em" }}>
                      <span>E16S</span>
                    </p>
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    1.0 Units
                  </td>
                </tr>

                {/* Row 7: B16 */}
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0", paddingLeft: "1.5em" }}>
                      <span>B16</span>
                    </p>
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    1.0 Units
                  </td>
                </tr>

                {/* Row 8: CF16SNU */}
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0", paddingLeft: "1.5em" }}>
                      <span>CF16SNU</span>
                    </p>
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    2.0 Units
                  </td>
                </tr>

                {/* Row 9: Separator AFK HOSES */}
                <tr style={{ backgroundColor: "#eeeeee" }}>
                  <td colSpan={2} style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0" }}>
                      <strong>AFK HOSES ARQCODE</strong>
                    </p>
                  </td>
                </tr>

                {/* Row 10: SEPARATEUR HO1/8 */}
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0", paddingLeft: "1.5em" }}>
                      <span>SEPARATEUR HO1/8</span>
                    </p>
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    1.0 Units
                  </td>
                </tr>

                {/* Row 11: Separator Autres pieces */}
                <tr style={{ backgroundColor: "#eeeeee" }}>
                  <td colSpan={2} style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0" }}>
                      <strong>Autres pièces</strong>
                    </p>
                  </td>
                </tr>

                {/* Row 12: ADAPTATEURS */}
                <tr>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    <p style={{ margin: "0", paddingLeft: "1em" }}>
                      <span>ADAPTATEURS</span>
                    </p>
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px" }}>
                    3.0 Units
                  </td>
                </tr>
              </tbody>
            </table>

            {/* .table for Additional Infos exactly formatted like report_template.html */}
            {reports && reports.filter(r => r.area === "ADDITIONAL_INFOS").length > 0 ? (
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "20px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f2f2f2" }}>
                    <th colSpan={2} style={{ border: "1px solid #ccc", padding: "5px", textAlign: "left", fontWeight: "bold", fontSize: "13px" }}>Compléments d'informations</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.filter(r => r.area === "ADDITIONAL_INFOS").sort((a, b) => a.order - b.order).map((report, idx) => (
                    <tr key={idx}>
                      <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px", width: "50%", color: "#374151" }}>{labelLookup[report.data] || report.data}</td>
                      <td style={{ border: "1px solid #ccc", padding: "5px", fontSize: "13px", color: "#111827" }}>{getMockValue(report.data, currentLanguage) || report.example || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}

            {/* Dummy Signatures exactly shaped like report_template.html */}
            <div style={{ marginTop: "30px", borderTop: "1px solid #ccc", paddingTop: "20px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ width: "100%", padding: "10px", textAlign: "center", verticalAlign: "middle" }}>
                      <h4 style={{ fontSize: "12px", fontWeight: "bold", margin: "0 0 5px 0", textTransform: "uppercase" }}>Signatures après</h4>
                      <div style={{ border: "1px solid #ccc", height: "100px", maxWidth: "400px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#fafafa", borderRadius: "4px" }}>
                        <span style={{ fontSize: "11px", color: "#9ca3af", fontStyle: "italic" }}>[Signature Client]</span>
                      </div>
                      <div style={{ marginTop: "5px", fontSize: "11px", color: "#4b5563" }}>
                        <p style={{ margin: "2px 0" }}>David Kaya</p>
                        <p style={{ margin: "2px 0", fontStyle: "italic" }}>Responsable maintenance</p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* .footer centered content exactly like report_template.html */}
            <div style={{ marginTop: "30px", paddingTop: "15px", borderTop: "1px solid #eee", textAlign: "center", fontSize: "12px", color: "gray" }}>
              {reports && reports.filter(r => r.area === "FOOTER").length > 0 ? (
                reports.filter(r => r.area === "FOOTER").sort((a, b) => a.order - b.order).map((report, idx) => (
                  <p key={idx} style={{ margin: "3px 0" }}>
                    <span style={{ fontWeight: "600" }}>{labelLookup[report.data] || report.data}</span>
                    <span>: {getMockValue(report.data, currentLanguage) || report.example || "-"}</span>
                  </p>
                ))
              ) : (
                <p style={{ fontStyle: "italic" }}>CHRONO FLEX BELGIUM SA/NV - TVA BE 0123.456.789 - RPR Bruxelles</p>
              )}
            </div>

          </div>
          {/* eslint-enable i18next/no-literal-string */}
        </DialogBody>
        <DialogFooter className="border-t border-gray-200 pt-4 shrink-0 px-6">
          <Button variant="filled" color="blue" onClick={() => setIsPreviewOpen(false)} className="normal-case">
            {t("common:close", "Close")}
          </Button>
        </DialogFooter>
      </Dialog>

      {isDeleteTemplateOpen && activeTemplate && templates && (
        <DeleteReportTemplateModal
          open={isDeleteTemplateOpen}
          template={activeTemplate}
          allTemplates={templates}
          onDelete={() => {
            setSelectedTemplateId(null);
            loadTemplates();
          }}
          onClose={() => setIsDeleteTemplateOpen(false)}
        />
      )}
    </div>
  );
}

const MOCK_DATA: Record<string, any> = {
  "technician": "Kurt JENSEN",
  "technician.van.name": "VAN12 - RF342TU",
  "applicant": "Michal MARKUS",
  "ref": "INTER-20260612-15",
  "customer.name": "BERGERAT MONNOYEUR",
  "customer.contactInfo.phone": "+33 6 89 89 00 00",
  "customer.contactInfo.mail": "livia.parker@bm.cat",
  "customer.contactInfo.mobile": "+33 6 77 00 11 22",
  "customer.nip": "BE0456123789",
  "purchaseOrderNumber": "PO-999882",
  "address": "The Dead End Road, 999, ROYAL ENFIELD",
  "customer.address": "The Dead End Road, 999, ROYAL ENFIELD",
  "intervention_creation": "12/06/2026 14:43",
  "schedule.start": "12/06/2026 15:25",
  "schedule.end": "12/06/2026 16:25",
  "time.start": "12/06/2026 15:17",
  "time.end": "12/06/2026 16:05",
  "intervention_real_start": "12/06/2026 15:17",
  "intervention_real_end": "12/06/2026 16:05",
  "travelTime.start": "12/06/2026 14:41",
  "travelTime.end": "12/06/2026 15:17",
  "onCallDuty": {
    "it": "No", "de": "Nein", "nl": "Nee", "nl-BE": "Nee", "es": "No", "ca": "No", "en": "No", "fr": "Non", "ro": "Nu", "pt": "Não", "pl": "Nie"
  },
  "origin.label": {
    "it": "Telefonata", "de": "Telefonanruf", "nl": "Telefoongesprek", "nl-BE": "Telefoongesprek", "es": "Llamada telefónica", "ca": "Trucada telefònica", "en": "Phone Call", "fr": "Appel téléphonique", "ro": "Apel telefonic", "pt": "Telefonema", "pl": "Połączenie telefoniczne"
  },
  "type.label": {
    "it": "Urgenza", "de": "Dringlichkeit", "nl": "Urgentie", "nl-BE": "Urgentie", "es": "Urgencia", "ca": "Urgència", "en": "Urgency", "fr": "Urgence", "ro": "Urgență", "pt": "Urgência", "pl": "Pilne"
  },
  "notes": {
    "it": "Perdita sul flessibile del carrello elevatore\nPrevedere dell'olio per il rabbocco dei livelli",
    "de": "Leck am Schlauch des Gabelstaplers\nÖl zum Nachfüllen bereitstellen",
    "nl": "Lekkage aan de slang van de vorkheftruck\nZorg voor olie om het niveau bij te vullen",
    "nl-BE": "Lek aan de flexibel van de vorkheftruck\nVoorzie olie om het niveau bij te vullen",
    "es": "Fuga en la manguera de la carretilla elevadora\nPrever aceite para rellenar los niveles",
    "ca": "Fuga en el flexible del stivador\nPreveure oli per a reposar els nivells",
    "en": "Leak on forklift hose\nProvide oil to top up levels",
    "fr": "Fuite sur le flexible du chariot élévateur\nPrévoir de l'huile pour faire l'appoint des niveaux",
    "ro": "Scurgere la furtunul stivuitorului\nAsigurați ulei pentru completarea nivelurilor",
    "pt": "Fuga na mangueira da empilhadeira\nPrever óleo para completar os níveis",
    "pl": "Wyciek na wężu wózka widłowego\nZapewnić olej do uzupełnienia poziomów"
  }
};

const getMockValue = (path: string, lang: string): string => {
  const val = MOCK_DATA[path];
  if (!val) return "";
  if (typeof val === "object") {
    return val[lang] || val[lang.split("-")[0]] || val["en"] || "";
  }
  return val;
};
